import React, { useMemo, useRef, useState } from 'react';
import * as Styled from '../styled';
import * as StyledAgent from './styled';
import { ShowAt, useScreenBreakpoint } from 'consts/breakpoints';
import Footer from 'components/static-footer';
import Breadcrumbs from 'components/breadcrumbs';
import { BreadcrumbsSection } from 'components/breadcrumbs/BreadcrumbsSection';
import Map from '../common/components/map';
import { Section, AUTO_SCROLL_DURATION } from 'components/navigation/subheader/SectionsNavigation';
import { noop, orderBy } from 'lodash';
import { ScrollSpyProvider, ScrollSpyTarget } from 'components/scroll-spy';
import { useIntersectionObserver } from 'hooks/useIntersectionObserver';
import { createPortal } from 'utils/universalPortal';
import { STICKY_ROOT } from 'consts/rootNodes';
import { Button } from 'ds/components/button';
import { createPhoneURI } from 'hooks/useDisplayPhone';
import { useLocale } from 'locale';
import PhoneIcon from 'assets/svg/phone-1-5.svg';
import { MobileMapSwitcher } from 'components/mobile-map-switcher/MobileMapSwitcher';
import { Gallery, Images } from '../common/sections/gallery';
import { Header } from '../common/sections/header';
import { Listing } from '../common/sections/listing';
import { PhoneButton } from '../common/sections/header/common/styled';
import { About } from '../common/sections/agent-about';
import { StaticContactForm } from '../common/sections/contact/static';
import { AgentOfficeContactModal } from '../common/sections/contact/popup';
import { Video } from '../common/sections/video';
import { OverflowShadow } from '../common/components/OverflowShadow';
import { Deals } from '../common/sections/deals';
import {
  IAgentPageData,
  IOfficeAward,
  IAgentOfficeBulletin,
  IOfficePageData,
  MarketplaceType,
  ResolutionPreference,
  IOfficeAgentDisplayLevel,
  IAgentOfficeBulletinsData,
  LeadingAgentData,
  LeadingAgentFeature,
  IAwardFeature,
} from 'utils/entities';
import { useImageUrlBuilder } from 'hooks/useImageUrlBuilder';
import { Awards } from '../common/sections/awards/Awards';
import { IAgentAbout, IContactInfo, OfficeBulletinType, PageDetails } from '../types';
import { UnitCardPrimaryParams, getAddress, getUnitCardPrimaryParams, renderAddress, unitCardTranslateKeys } from 'utils/addressFormatter';
import { Reviews } from '../common/sections/Reviews';
import { SocialRow } from '../common/sections/SocialRow';
import { getPoiListByType, sectionNameToEvent } from '../helper';
import { Social } from '../common/components/Social';
import withEvents from 'analytics/withEvents';
import LeadingAgentInfo from 'components/leading-agent-info';
import { checkEnabledLeadingAgentFeature, getLeadingAgentFeature } from 'helpers/contact';
import { Route, State as RouteState } from 'config/routes';
import ScrollIntoView from 'scroll-into-view';
import { HEADER_HEIGHT } from 'ds/components/Layout';
import { UiAgentOfficeDeal } from 'screens/AgentOfficePages/common/sections/deals/types';


export interface AgentPageProps {
  mainData: IAgentPageData;
  leadingAgentData: LeadingAgentData;
  officeData: IOfficePageData;
  listings: IAgentOfficeBulletinsData;
  setContactOpen?: (open: boolean) => void;
  isContacted: boolean;
  onPhoneExpose?: (props: PageDetails, isLeading: boolean) => void;
  route: RouteState;
  onScrollEvent?: (section: string, officeDetails: PageDetails) => void;
  agentDeals: UiAgentOfficeDeal[];
}

const AgentPage: React.FC<AgentPageProps> = ({ mainData, setContactOpen, officeData, route, listings, isContacted, onPhoneExpose, onScrollEvent = noop, leadingAgentData, agentDeals }) => {
  const { t } = useLocale();
  const isMobile = useScreenBreakpoint() === 1;
  const isDesktop = useScreenBreakpoint() >= 3;
  const [ activeSection, setActiveSection ] = useState(null);
  const [ contactVisible, setContactVisible ] = useState(null);
  const [ listingVisible, setListingVisible ] = useState(null);
  const [ showMap, setShowMap ] = useState(false);
  const contactSectionNode = useRef<HTMLDivElement>(null);
  const listingSectionNode = useRef<HTMLDivElement>(null);
  const constructImageURI = useImageUrlBuilder();

  useIntersectionObserver(([ entry ]) => {
    setContactVisible(!entry.isIntersecting);
  }, [ contactSectionNode ], { threshold: [ 0, 1 ] });

  useIntersectionObserver(([ entry ]) => {
    setListingVisible(!entry.isIntersecting);
  }, [ listingSectionNode ], { threshold: [ 0, 1 ] });

  const toggleMap = () => {
    setShowMap(!showMap);
  };

  const handleActiveSection = (section: Section) => {
    setActiveSection(section);
    onScrollEvent(section, eventData);
  };


  const topOffset = isMobile ? HEADER_HEIGHT * 2 : 52 + HEADER_HEIGHT;
  const scrollSettings = {
    time: AUTO_SCROLL_DURATION,
    align: {
      topOffset,
      top: 0,
    },
  };

  const {
    agentId,
    officeId,
    title,
    madadAwards,
    descriptionHtml,
    displayLevel,
    licenseId,
    licenseFromYear,
    name,
    iconUrl,
    virtualPhone,
    websiteUrl,
    linkedinUrl,
    instagramUrl,
    facebookPageUrl,
    testimonials,
    videoUrl,
    videoSource,
    emailAddress,
  } = mainData;

  const phone = officeData.officeConnectionInAgentPage || !virtualPhone ? officeData.virtualPhone : virtualPhone;
  const isPromoted = displayLevel === IOfficeAgentDisplayLevel.Premium;
  const officeLogo = officeData ? constructImageURI(officeData.iconUrl) : null;
  const officeName = officeData.officeName;
  const bulletinsList = listings && listings.bulletins || [];
  const commercialBulletinsList = listings && listings.commercialBulletins || [];
  const spotlightListingsList = listings && listings.spotlightListings || [];
  const bulletinsMap = listings && listings.bulletinsMap;
  const deals = listings && listings.deals || [];
  const hasBulletins = [ ...bulletinsList, ...commercialBulletinsList ].length;
  const showLeadingAgentLabel = leadingAgentData.isLeadingAgent ? checkEnabledLeadingAgentFeature(leadingAgentData.award, LeadingAgentFeature.LeadingStatement) : false;
  const leadingAgentFeature = leadingAgentData.isLeadingAgent ? getLeadingAgentFeature(leadingAgentData.award, LeadingAgentFeature.LeadingStatement) : {} as IAwardFeature;
  const showLeadingAgentSection = showLeadingAgentLabel ? leadingAgentData.isLeadingAgent && leadingAgentFeature.neighborhoods && leadingAgentFeature.neighborhoods.length && leadingAgentData.leadingStatement.length : false;

  React.useEffect(() => {
    if (route && route.params && route.params.scrollTo && hasBulletins) {
      const { scrollTo } = route.params;
      const targetScrollEl = document.querySelector<HTMLElement>(`[data-scroll-spy-id="${scrollTo}"`);
      if (targetScrollEl) {
        ScrollIntoView(targetScrollEl, scrollSettings);
      }
    }
  }, [ route, hasBulletins ]);

  const aboutInfo: IAgentAbout = {
    agentId,
    officeId,
    name,
    title,
    isLeadingAgent: showLeadingAgentLabel,
    description: descriptionHtml,
    logo: iconUrl,
    officeName,
    officeLogo,
    licenseId,
    licenseFromYear,
    phone,
  };
  const agentLicense = licenseFromYear && licenseId ? t('newAgent.license.info', { year: licenseFromYear, license: licenseId }) : null;

  const makeGalleryData = (list: IAgentOfficeBulletin[], bulletinType: OfficeBulletinType): Images[] => list.reduce((acc, poi) => {
    if (acc.length === 10) return acc;
    const imgParams = isMobile ? {} : (
      list.length === 1 ? { height: 360, width: 1142 } : { height: 360, width: 360 }
    );
    return [
      ...acc,
      {
        img: poi.images && poi.images.length && constructImageURI(poi.images[0].imageUrl, imgParams),
        address: getAddress<UnitCardPrimaryParams>(t, renderAddress)(poi.structuredAddress, unitCardTranslateKeys, poi.structuredAddress.resolutionPreferences || ResolutionPreference.Accurate, getUnitCardPrimaryParams),
        id: poi.id,
        dealType: poi.dealType,
        bulletinType,
      },
    ];
  }, []);

  const bulletinsForGallery = useMemo(() => getPoiListByType(bulletinsList, spotlightListingsList, MarketplaceType.Residential, bulletinsMap), [ bulletinsList, spotlightListingsList, bulletinsMap ]);
  const commercialBulletinsForGallery = useMemo(() => getPoiListByType(commercialBulletinsList, spotlightListingsList, MarketplaceType.Commercial, bulletinsMap), [ commercialBulletinsList, spotlightListingsList, bulletinsMap ]);

  const galleryData: Images[] = hasBulletins ? [ ...makeGalleryData(bulletinsForGallery, 'bulletin'), ...makeGalleryData(commercialBulletinsForGallery, 'commercialBulletin') ] : [];

  const contactInfo: IContactInfo = {
    name,
    logo: iconUrl,
    title,
    phone,
  };

  const hasSocial = Boolean(websiteUrl || linkedinUrl || facebookPageUrl || instagramUrl);

  const sectionsWithData = {
    [Section.ABOUT]: true,
    [Section.OVERVIEW]: Boolean(isPromoted && galleryData.length) || undefined,
    [Section.LISTINGS]: Boolean(hasBulletins) || undefined,
    [Section.DEALS]: (!officeData.hideDealsOnPublicSite && Boolean(agentDeals && agentDeals.length)) || undefined,
    [Section.REVIEWS]: Boolean(isPromoted && testimonials && testimonials.length) || undefined,
  };

  const sections: Section[] = Object.keys(sectionsWithData).map((section: Section) => sectionsWithData[section] ? section : undefined).filter(Boolean);
  const sortAwards = (arr: IOfficeAward[]) => orderBy(arr, [ 'year', 'badgeType' ], [ 'desc', 'desc' ]);
  const hideContactForm = !(
    (madadAwards && madadAwards.length && isPromoted)
    || (isPromoted && galleryData.length)
    || (isPromoted && videoUrl && videoSource)
    || (hasBulletins || (agentDeals && agentDeals.length))
    || (isPromoted && testimonials && testimonials.length)
    || isMobile
  );

  const eventData: PageDetails = {
    agent_details: {
      id: agentId,
      name,
      is_free: !isPromoted,
      phone_number: phone,
      email: emailAddress,
    },
    office_details: {
      id: officeId,
      name: officeName,
      is_free: !isPromoted,
    },
  };

  const breadcrumbItems = useMemo(() => [ {
    label: t('madad.navigation.breadcrumbs.home'),
    routeName: Route.Home,
  }, {
    label: t('html.madadAgentSearch.title'),
    routeName: Route.MadadPage,
  }, {
    label: officeName,
    routeName: Route.OfficePage,
    routeParams: { id: officeId, source: 'agent_page' },
  }, {
    label: name,
    isCurrent: true,
  } ], [ t, officeId, officeName, name ]);

  const awardsVideoContent = isPromoted ? (
    <>
      {madadAwards && madadAwards.length ? (
        <Styled.SectionSpace  desktop={{ top: 3, bottom: 5 }} tablet={{ top: 3 }} mobile={{ top: 3, bottom: 3 }}>
          <OverflowShadow>
            <Awards awards={sortAwards(madadAwards)} withGrayBg={isDesktop} />
          </OverflowShadow>
        </Styled.SectionSpace>
      ) : null}
      {isPromoted && galleryData.length && !isMobile ? (
        <ScrollSpyTarget targetId={Section.OVERVIEW}>
          <Styled.SectionSpace desktop={{ bottom: 7 }} tablet={{ top: 3, bottom: 4 }}>
            <Gallery images={galleryData} grayShadow withTitle pageDetails={eventData} />
          </Styled.SectionSpace>
        </ScrollSpyTarget>
      ) : null}
      {isPromoted && videoUrl && videoSource ? (
        <ScrollSpyTarget targetId={Section.VIDEO}>
          <Styled.SectionSpace desktop={{ bottom: 10 }} tablet={{ bottom: 10 }} mobile={{ top: 3, bottom: 5 }}>
            <Video videoUrl={videoUrl} videoSource={videoSource} pageDetails={eventData} />
          </Styled.SectionSpace>
        </ScrollSpyTarget>
      ) : null}
    </>
  ) : null;

  return (
    <>
      {showMap ? null : (
        <Header
          agentId={agentId}
          logo={iconUrl}
          title={name}
          phone={phone}
          isLeadingAgent={showLeadingAgentLabel}
          sections={sections}
          activeSection={activeSection}
          officeName={officeName}
          officeLogo={officeLogo}
          agentSubtitle={agentLicense}
          officeId={officeId}
          pageDetails={eventData}
        />
      )}
      <ScrollSpyProvider onChange={handleActiveSection}>
        <StyledAgent.GrayArea>
          {isMobile && isPromoted && galleryData.length ? (
            <ScrollSpyTarget targetId={Section.OVERVIEW}>
              <Styled.SectionSpace desktop={{ bottom: 7 }} tablet={{ top: 3, bottom: 4 }}>
                <Gallery images={galleryData} grayShadow pageDetails={eventData} />
              </Styled.SectionSpace>
            </ScrollSpyTarget>
          ) : null}

          {isMobile && leadingAgentData.isLeadingAgent && showLeadingAgentSection ? (
            <ScrollSpyTarget targetId={Section.LeadingStatement}>
              <Styled.SectionSpace mobile={{ top: 5 }}>
                <LeadingAgentInfo isMobile agentStatement={leadingAgentData.leadingStatement} neighbourhoods={leadingAgentFeature.neighborhoods} />
              </Styled.SectionSpace>
            </ScrollSpyTarget>
          ) : null}

          <ScrollSpyTarget targetId={Section.ABOUT}>
            <Styled.SectionSpace  desktop={{ top: 4, bottom: 4 }} tablet={{ top: 3 }} mobile={{ top: 3, bottom: 3 }}>
              <About {...aboutInfo} pageDetails={eventData} />
            </Styled.SectionSpace>
          </ScrollSpyTarget>

          {!isMobile && leadingAgentData.isLeadingAgent && showLeadingAgentSection ? (
            <ScrollSpyTarget targetId={Section.LeadingStatement}>
              <Styled.SectionSpace tablet={{ top: 3 }}>
                <LeadingAgentInfo agentStatement={leadingAgentData.leadingStatement} neighbourhoods={leadingAgentFeature.neighborhoods} />
              </Styled.SectionSpace>
            </ScrollSpyTarget>
          ) : null}

          {awardsVideoContent}
        </StyledAgent.GrayArea>
        {hasBulletins || (deals && deals.length) ? (
          <ScrollSpyTarget targetId={Section.LISTINGS}>
            {hasBulletins ? (
              <Styled.SectionSpace desktop={{ top: 5, bottom: 8 }} tablet={{ bottom: 12 }} mobile={{ top: 7, bottom: 4 }} ref={listingSectionNode}>
                <Listing pageDetails={eventData} />
              </Styled.SectionSpace>
            ) : null}
            <Styled.SectionSpace desktop={{ bottom: 8 }}>
              <Map
                showMobileMap={showMap}
                setShowMobileMap={toggleMap}
                activeSection={Section.OVERVIEW}
                hasPaddingBottom={false}
              />
            </Styled.SectionSpace>
          </ScrollSpyTarget>
        ) : null}
        {!officeData.hideDealsOnPublicSite && agentDeals && agentDeals.length ? (
          <ScrollSpyTarget targetId={Section.DEALS}>
            <Styled.SectionSpace desktop={{ bottom: 15 }} tablet={{ bottom: 11 }} mobile={{ bottom: 4 }}>
              <Deals data={agentDeals} />
            </Styled.SectionSpace>
          </ScrollSpyTarget>
        ) : null}
        {isPromoted && testimonials && testimonials.length ? (
          <ScrollSpyTarget targetId={Section.REVIEWS}>
            <Styled.SectionSpace desktop={{ bottom: 8 }} tablet={{ bottom: 4 }} mobile={{ bottom: 4 }}>
              <Reviews
                list={testimonials}
                pageDetails={eventData}
              />
            </Styled.SectionSpace>
          </ScrollSpyTarget>
        ) : null}
        {hideContactForm ? null : (
          <ScrollSpyTarget targetId={Section.CONTACT}>
            <Styled.SectionSpace desktop={{ bottom: 10 }} tablet={{ bottom: 4 }} mobile={{ bottom: 5 }} ref={contactSectionNode}>
              <StaticContactForm {...contactInfo} isLeadingAgent={showLeadingAgentLabel} isAgent id={agentId} />
              {isMobile ? null : <Styled.SocialWrapper><Social name={name} source="footer" pageDetails={eventData} /></Styled.SocialWrapper>}
            </Styled.SectionSpace>
          </ScrollSpyTarget>
        )}
        <ScrollSpyTarget targetId={Section.FOOTER}>
          {isPromoted && hasSocial ? (
            <Styled.SectionSpace desktop={{ bottom: 9 }} tablet={{ bottom: 9 }} mobile={{ bottom: 3 }} ref={contactSectionNode}>
              <SocialRow web={websiteUrl} lIn={linkedinUrl} fb={facebookPageUrl} instagram={instagramUrl} />
            </Styled.SectionSpace>
          ) : null}
          <Styled.FooterWrapper>
            <Footer
              noBorderTop={isMobile}
              breadcrumbs={
                isMobile ? (
                  <BreadcrumbsSection>
                    <Breadcrumbs items={breadcrumbItems} isFooter />
                  </BreadcrumbsSection>
                ) : null
              }
            />
          </Styled.FooterWrapper>
        </ScrollSpyTarget>
      </ScrollSpyProvider>
      <ShowAt to={2}>
        {(contactVisible || !listingVisible) && !showMap ? createPortal(
          <Styled.MobileContactWrapper>
            {(hasBulletins || (deals && deals.length)) && !listingVisible ? (
              <Styled.MapSwitcherWrapper>
                <MobileMapSwitcher toggleMap={toggleMap} isMobileMapViewActive={showMap} />
              </Styled.MapSwitcherWrapper>
            ) : null}
            <ShowAt at={1}>
              {contactVisible ? (
                <Styled.MobileNavSectionWrapper>
                  <Styled.MobileContactButtonWrapper>
                    <Button disabled={isContacted} mode={isContacted ? undefined : 'ghost'} fullWidth size="large" onClick={() => setContactOpen(true)}>
                      {isContacted ? <Styled.Checkmark /> : null}
                      {t(isContacted ? 'agent.office.contact.button.contacted' : 'contactAgentModal.mobileButton.modeB')}
                    </Button>
                  </Styled.MobileContactButtonWrapper>
                  {phone ? (
                    <Styled.MobileContactButtonWrapper>
                      <PhoneButton onClick={() => onPhoneExpose(eventData, showLeadingAgentLabel)} href={createPhoneURI(phone)} size="large" fullWidth disabled={false}>
                        {t('contactAgentModal.callButton.modeB')}
                        <PhoneIcon />
                      </PhoneButton>
                    </Styled.MobileContactButtonWrapper>
                  ) : null}
                </Styled.MobileNavSectionWrapper>
              ) : null}
            </ShowAt>
          </Styled.MobileContactWrapper>, STICKY_ROOT
        ) : null}
      </ShowAt>
      <AgentOfficeContactModal isLeadingAgent={showLeadingAgentLabel} contactData={contactInfo} id={agentId} />
    </>
  );
};

export default withEvents<AgentPageProps>((sendEvent) => ({
  onPhoneExpose: (props: PageDetails, isLeadingAgent: boolean) => {
    sendEvent('user_contact_me_call_submit', 'user', {
      property: {
        property_id: 'property_id',
        deal_type: 'for_sale',
        bulletin_info: {
          agent_details: {
            id: props.agent_details.id,
            name: props.agent_details.name,
            office_id: props.agent_details.office_id,
            office_name: props.office_details.name,
            phone_number: props.agent_details.phone_number,
            is_leading_agent: isLeadingAgent,
          },
        },
      },
      event: {
        source: 'agent_page',
      },
      rtbData: {
        phone: props.agent_details.phone_number,
      },
    });
  },
  onScrollEvent: (section: Section, officeDetails: PageDetails) => {
    if (section !== Section.FOOTER) {
      sendEvent('agent_scroll_to_section', 'agent', {
        ...officeDetails,
        event: {
          page_section: sectionNameToEvent(section),
        },
      });
    }
  },
}))(AgentPage);
